import { call, put, takeLatest, select } from "redux-saga/effects";
import { getDashboardData } from "../../common/apiV2";
import {
  DASHBOARD_LOAD_REQUEST,
  loadDashboardDataSuccess,
  loadDashboardDataError
} from "./actions";
import { normalizeDashboardRows, resolveWarehouseScansRow } from "./warehouseScans";

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Kalenderwoche: Montag bis Sonntag (Ende exklusiv)
const startOfCalendarWeek = (date) => {
  const d = startOfDay(date);
  const day = d.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMonday);
  return d;
};

const getLastMonthRange = (referenceDate) => {
  const ref = new Date(referenceDate);
  return {
    start: new Date(ref.getFullYear(), ref.getMonth() - 1, 1),
    end: new Date(ref.getFullYear(), ref.getMonth(), 1)
  };
};

const getComparisonRange = ({ startDate, endDate, rangeKey }) => {
  switch (rangeKey) {
    case "today":
      return {
        start: addDays(startDate, -1),
        end: new Date(startDate)
      };
    case "yesterday":
      return {
        start: addDays(startDate, -1),
        end: new Date(startDate)
      };
    case "thisWeek":
      return {
        start: addDays(startDate, -7),
        end: new Date(startDate)
      };
    case "lastWeek":
      return {
        start: addDays(startDate, -7),
        end: new Date(startDate)
      };
    case "thisMonth":
      return {
        start: new Date(startDate.getFullYear(), startDate.getMonth() - 1, 1),
        end: new Date(startDate.getFullYear(), startDate.getMonth(), 1)
      };
    case "lastMonth":
      return {
        start: new Date(startDate.getFullYear(), startDate.getMonth() - 1, 1),
        end: new Date(startDate.getFullYear(), startDate.getMonth(), 1)
      };
    case "thisYear":
      return {
        start: new Date(startDate.getFullYear() - 1, 0, 1),
        end: new Date(startDate.getFullYear(), 0, 1)
      };
    case "custom":
    default:
      // Standardvergleich: immer kompletter letzter Monat (1. Vormonat bis 1. aktueller Monat)
      return getLastMonthRange(endDate);
  }
};

const formatApiSeries = (companies) => {
  const rows = normalizeDashboardRows(companies);

  const usersPerCompany = rows.map(company => ({
    companyId: company.logisticcompany_id,
    companyName: company.name,
    userCount: company.nutzer,
    members: Array.isArray(company.members) ? company.members : []
  }));

  const statusUpdates = rows.map(company => ({
    companyId: company.logisticcompany_id,
    companyName: company.name,
    statusCount: company.totalStatus
  }));

  const oneTimeAppPerCompany = rows.map(company => ({
    companyId: company.logisticcompany_id,
    companyName: company.name,
    apps: company.apps ?? 0,
    sms: company.sms ?? 0,
    liveTracking: company.liveTracking ?? 0
  }));

  const warehouseScansRow = resolveWarehouseScansRow(rows);

  return {
    usersPerCompany,
    statusUpdates,
    oneTimeAppPerCompany,
    warehouseScansRow
  };
};

function* loadDashboardData({ payload }) {
  try {
    // Get token from state (assuming it's available in auth state)
    const state = yield select();
    const token = state.auth?.token;
    
    if (!token) {
      throw new Error("No authentication token available");
    }

    // Use provided dates or default to current month (1. bis heute)
    const now = new Date();
    const endDate = payload?.endDate ? new Date(payload.endDate) : now;
    const startDate = payload?.startDate
      ? new Date(payload.startDate)
      : new Date(now.getFullYear(), now.getMonth(), 1);
    const rangeKey = payload?.rangeKey || "custom";
    const comparisonRange = getComparisonRange({ startDate, endDate, rangeKey });
    
    // Format dates as required by API (YYYY-MM-DD HH:mm:ss)
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    try {
      const startDateFormatted = formatDate(startDate);
      const endDateFormatted = formatDate(endDate);
      const comparisonStartDateFormatted = formatDate(comparisonRange.start);
      const comparisonEndDateFormatted = formatDate(comparisonRange.end);
      
      console.log("Dashboard API call:", {
        startDate: startDateFormatted,
        endDate: endDateFormatted
      });
      console.log("Dashboard comparison API call:", {
        startDate: comparisonStartDateFormatted,
        endDate: comparisonEndDateFormatted,
        rangeKey
      });
      
      const apiData = yield call(getDashboardData, {
        startDate: startDateFormatted,
        endDate: endDateFormatted
      });
      const comparisonApiData = yield call(getDashboardData, {
        startDate: comparisonStartDateFormatted,
        endDate: comparisonEndDateFormatted
      });

      console.log("Dashboard API response:", apiData);
      console.log("Dashboard comparison API response:", comparisonApiData);

      const companies = apiData.data;
      const comparisonCompanies = comparisonApiData.data;
      const currentSeries = formatApiSeries(companies);
      const comparisonSeries = formatApiSeries(comparisonCompanies);

      const dashboardData = {
        ...currentSeries,
        comparison: comparisonSeries,
        comparisonRange: {
          startDate: comparisonRange.start.toISOString(),
          endDate: comparisonRange.end.toISOString()
        },
        rawApiData: companies, // Keep original data for debugging
        rawComparisonApiData: comparisonCompanies,
        timestamp: new Date().toISOString(),
        source: "api"
      };

      yield put(loadDashboardDataSuccess(dashboardData));
    } catch (apiError) {
      console.error("API call failed:", apiError);
      console.error("API Error details:", {
        message: apiError.message,
        status: apiError.status,
        response: apiError.response
      });
      
      // For now, let's not fall back to mock data - show the error instead
      yield put(loadDashboardDataError(apiError.message));
    }
  } catch (error) {
    console.error("Failed to load dashboard data:", error);
    yield put(loadDashboardDataError(error.message));
  }
}

export default function* dashboardSaga() {
  yield takeLatest(DASHBOARD_LOAD_REQUEST, loadDashboardData);
}
